import {
  CreateRoleRequest,
  CreateUserOperationRequest,
  CreateUserRequest,
  DeleteUserInvitationRequest,
  DeleteUserRequest,
  DisplayUser,
  GetAvailableModuleScopesRequest,
  GetMyScopesRequest,
  GetRolesRequest,
  GetUserMetaRequest,
  GetUsersRequest,
  ModuleWithScopes,
  ResendInvitationOperationRequest,
  ResendInvitationRequest,
  RoleWithFullScopes,
  SetUserMetaRequest,
  UpdateRoleRequest,
  UpdateUserOperationRequest,
  UpdateUserRequest,
  UpsertRoleRequest,
  UserMeta,
  UsersApi,
} from '@Generated/back-office-client/src'
import { BackOfficeDataServiceAPI } from './back-office-data-service'

const getCurrentUserScopes = async () => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: GetMyScopesRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }

  return await api.getMyScopes(request)
}

const getCurrentUserMeta = async (): Promise<UserMeta> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: GetUserMetaRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }

  return await api.getUserMeta(request)
}

const setCurrentUserMeta = async (userMeta: UserMeta): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: SetUserMetaRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    userMeta,
  }

  return await api.setUserMeta(request)
}

const getAllUsersInOrg = async (): Promise<DisplayUser[]> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: GetUsersRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }

  return await api.getUsers(request)
}

const createUser = async (createUserRequest: CreateUserRequest): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: CreateUserOperationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    createUserRequest,
  }

  await api.createUser(request)
}

const updateUser = async (
  authProviderId: string,
  updateUserRequest: UpdateUserRequest,
): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: UpdateUserOperationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    updateUserRequest,
    authProviderId,
  }

  return await api.updateUser(request)
}

const resendInvitation = async (
  resendInvitationRequest: ResendInvitationRequest,
): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: ResendInvitationOperationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    resendInvitationRequest,
  }

  return await api.resendInvitation(request)
}

const getCustomerRoles = async (): Promise<RoleWithFullScopes[]> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: GetRolesRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }

  return await api.getRoles(request)
}

const getCustomerScopes = async (): Promise<ModuleWithScopes[]> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: GetAvailableModuleScopesRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }

  return (await api.getAvailableModuleScopes(request)).moduleWithScopes
}

const createRole = async (upsertRoleRequest: UpsertRoleRequest): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: CreateRoleRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    upsertRoleRequest,
  }

  return await api.createRole(request)
}

const updateRole = async (roleId: string, upsertRoleRequest: UpsertRoleRequest): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: UpdateRoleRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    roleId,
    upsertRoleRequest,
  }

  return await api.updateRole(request)
}

const deleteUser = async (authProviderId: string): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: DeleteUserRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    authProviderId,
  }

  return await api.deleteUser(request)
}

const deleteUserInvitation = async (authProviderId: string): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(UsersApi)
  const request: DeleteUserInvitationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    authProviderId,
  }

  return await api.deleteUserInvitation(request)
}

export const UsersServiceAPI = {
  createUser,
  deleteUser,
  deleteUserInvitation,
  createRole,
  updateRole,
  getAllUsersInOrg,
  getCustomerRoles,
  getCustomerScopes,
  getCurrentUserScopes,
  getCurrentUserMeta,
  setCurrentUserMeta,
  resendInvitation,
  updateUser,
}
